import { Icon } from '../components/ui/Icon';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { PublicAccessShell } from '../components/PublicAccessShell';

const RESEND_COOLDOWN_SECONDS = 60;

export function LoginPage() {
  const navigate = useNavigate();
  const { user, login, verifyLogin2fa, resendLogin2fa } = useAuth();

  // Step 1: credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 2: 2FA
  const [step, setStep] = useState<'creds' | '2fa'>('creds');
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState<'TOTP' | 'EMAIL' | undefined>(undefined);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  // Resend cooldown (EMAIL method only)
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [resendError, setResendError] = useState('');

  // Clear cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = () => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, remember);
      if (result.twoFactorRequired) {
        setTwoFactorToken(result.twoFactorToken);
        setTwoFactorMethod(result.method);
        setStep('2fa');
        setCode('');
        setUseRecovery(false);
        setResendError('');
        // For EMAIL method, the server sends a code on login — start cooldown immediately
        if (result.method === 'EMAIL') {
          startCooldown();
        }
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd logowania');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verifyLogin2fa(twoFactorToken, code, remember);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nieprawidłowy kod');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendError('');
    setLoading(true);
    try {
      await resendLogin2fa(twoFactorToken);
      startCooldown();
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Odczekaj chwilę przed ponownym wysłaniem');
    } finally {
      setLoading(false);
    }
  };

  const isEmailMethod = twoFactorMethod === 'EMAIL';

  return (
    <PublicAccessShell
      title="Zaloguj się do dokumentacji terenowej."
      description="Wracasz do projektów, markerów, formularzy i raportów."
      sideTitle="Plan, statusy i raport w jednym miejscu."
      sideDescription="Pinveo spina rzut obiektu, materiał zdjęciowy, formularze i eksporty tak, żeby dane z terenu nie rozpadały się na osobne foldery i notatki."
      sideItems={[
        'markery na rzucie',
        'formularze offline',
        'raport PDF / Excel / ZIP',
        'dostęp klienta',
      ]}
    >
      {step === 'creds' ? (
        <form
          onSubmit={handleSubmit}
        >
          {error && (
            <div className="mb-4 rounded-xl bg-[var(--err-bg)] p-3 text-sm text-err">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="email"
              className="mb-1 block text-sm font-medium text-ink-soft"
            >
              Email
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-muted">
                <Icon name="envelope" className="h-4 w-4" />
              </span>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="field-input pl-9"
                placeholder="email@example.com"
              />
            </div>
          </div>

          <div className="mb-4">
            <label
              htmlFor="password"
              className="mb-1 block text-sm font-medium text-ink-soft"
            >
              Hasło
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-muted">
                <Icon name="lock" className="h-4 w-4" />
              </span>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="field-input pl-9 pr-10"
                placeholder="Wprowadź hasło"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted hover:text-ink-soft"
                aria-label={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                tabIndex={-1}
              >
                <Icon name={showPassword ? 'eye' : 'eye'} className={`h-4 w-4 ${showPassword ? 'text-primary-600' : ''}`} />
              </button>
            </div>
          </div>

          <div className="mb-6 flex items-center justify-between">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-line text-primary-600 focus:ring-primary-500"
              />
              <span>Zapamiętaj mnie</span>
            </label>
            <Link
              to="/forgot-password"
              className="text-sm text-primary-600 hover:underline"
            >
              Zapomniałem hasła
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? 'Logowanie...' : 'Zaloguj się'}
          </button>

          <div className="mt-6 border-t border-line pt-4 text-center text-sm">
            <span className="text-muted">Nie masz konta? </span>
            <Link to="/request-access" className="font-medium text-primary-600 hover:underline">
              Poproś o wczesny dostęp
            </Link>
          </div>
        </form>
      ) : (
        <form
          onSubmit={handleVerify2fa}
        >
          <div className="mb-4 flex items-center gap-2 text-ink">
            <Icon name="check" className="h-5 w-5 text-primary-600" />
            <h2 className="text-base font-semibold">Weryfikacja dwuetapowa</h2>
          </div>

          {!useRecovery && (
            <p className="mb-4 text-sm text-muted">
              {isEmailMethod
                ? 'Wysłaliśmy 6-cyfrowy kod na Twój e-mail.'
                : 'Wprowadź 6-cyfrowy kod z aplikacji authenticator.'}
            </p>
          )}
          {useRecovery && (
            <p className="mb-4 text-sm text-muted">
              Wprowadź jeden z kodów zapasowych (format XXXX-XXXX).
            </p>
          )}

          {error && (
            <div className="mb-4 rounded-xl bg-[var(--err-bg)] p-3 text-sm text-err">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="2fa-code"
              className="mb-1 block text-sm font-medium text-ink-soft"
            >
              {useRecovery ? 'Kod zapasowy' : (isEmailMethod ? 'Kod z e-maila' : 'Kod z aplikacji')}
            </label>
            <input
              id="2fa-code"
              type="text"
              autoFocus
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="field-input tracking-widest"
              placeholder={useRecovery ? 'XXXX-XXXX' : '000000'}
              maxLength={useRecovery ? 9 : 6}
              inputMode={useRecovery ? 'text' : 'numeric'}
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length === 0}
            className="btn-primary w-full"
          >
            {loading ? 'Weryfikacja...' : 'Zweryfikuj'}
          </button>

          {/* Resend button — only for EMAIL method, not when using recovery */}
          {isEmailMethod && !useRecovery && (
            <div className="mt-3">
              {resendError && (
                <p className="mb-2 text-center text-xs text-err">{resendError}</p>
              )}
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || loading}
                className="w-full text-sm text-primary-600 hover:underline disabled:cursor-not-allowed disabled:text-muted disabled:no-underline"
              >
                {resendCooldown > 0
                  ? `Wyślij ponownie (${resendCooldown} s)`
                  : 'Wyślij ponownie'}
              </button>
            </div>
          )}

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                setUseRecovery((v) => !v);
                setCode('');
                setError('');
              }}
              className="text-sm text-primary-600 hover:underline"
            >
              {useRecovery
                ? (isEmailMethod ? 'Wróć do kodu z e-maila' : 'Wróć do kodu z aplikacji')
                : 'Użyj kodu zapasowego'}
            </button>
          </div>

          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => {
                setStep('creds');
                setCode('');
                setError('');
                setTwoFactorToken('');
                setTwoFactorMethod(undefined);
                setResendCooldown(0);
                setResendError('');
                if (cooldownRef.current) {
                  clearInterval(cooldownRef.current);
                  cooldownRef.current = null;
                }
              }}
              className="text-sm text-muted hover:text-ink-soft"
            >
              ← Wróć do logowania
            </button>
          </div>
        </form>
      )}
    </PublicAccessShell>
  );
}
